import { useState, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Text, Field, InputDropdown, Icon, PrimaryButton, Button, InputV2, Alert } from '@artprov/design-system'
import SafeSearchBar from '@/common/components/SafeSearchBar'
import { useMiPerfilForm } from './hooks/useMiPerfilForm.js'
import { useCargosAreas } from './hooks/useCargosAreas.js'
import { useCuentasUsuario } from './hooks/useCuentasUsuario.js'
import { useDatosPersonales } from './hooks/useDatosPersonales.js'
import { TIPOS_TELEFONO, FORM_LABELS, FORM_PLACEHOLDERS, TOAST_MESSAGES } from './constants/formConstants.js'
import CuentasTable from './components/CuentasTable'
import ModalAsociarCuenta from './components/ModalAsociarCuenta'
import ModalDescartarCambios from './components/ModalDescartarCambios'
import ModalGuardarCambios from './components/ModalGuardarCambios'
import { buildCuentasRows } from './util/buildCuentasRows'
import { buildDatosPersonalesPayload, buildAsociarCuentaPayload } from './util/mappers'
import { updateDatosPersonales, asociarCuenta } from '@/common/utils/services/usuarios.service'
import { getIdUsuario, getEmailUsuario } from '@/auth/auth.utils'
import { useIsMobileMenu } from '@/common/ds/Menu'

export default function MiPerfil() {
  const navigate = useNavigate()
  const isMobile = useIsMobileMenu()
  const { openMobileProfile } = useOutletContext() ?? {}

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isConfirmSaveModalOpen, setIsConfirmSaveModalOpen] = useState(false)

  const [isModalAsociarOpen, setIsModalAsociarOpen] = useState(false)
  const [isAsociandoCuenta, setIsAsociandoCuenta] = useState(false)

  // Estado único de toast: { variant: 'success' | 'error', message: string } | null
  const [toast, setToast] = useState(null)

  // Estado para loading del guardado
  const [isSaving, setIsSaving] = useState(false)

  const { cargoOptions, areaOptions } = useCargosAreas()

  // Hook para datos personales del usuario
  const {
    apellido: apellidoReal,
    nombre: nombreReal,
    correoElectronico: correoReal,
    tipoTelefonoPrincipal: tipoTelPrincipalReal,
    numeroTelefonoPrincipal: numTelPrincipalReal,
    tipoTelefonoAlternativo: tipoTelAlternativoReal,
    numeroTelefonoAlternativo: numTelAlternativoReal,
    tipoTelefonoPrincipalId: tipoTelPrincipalIdReal,
    updateDatos: updateDatosPersonalesState,
  } = useDatosPersonales()

  // Datos crudos del usuario para el payload de asociación de cuenta
  const datosPersonales = useMemo(
    () => ({
      correoElectronico: correoReal,
      apellido: apellidoReal,
      nombre: nombreReal,
      tipoTelefonoPrincipalId: tipoTelPrincipalIdReal,
      numeroTelefonoPrincipal: numTelPrincipalReal,
    }),
    [correoReal, apellidoReal, nombreReal, tipoTelPrincipalIdReal, numTelPrincipalReal]
  )

  const {
    contratos,
    headers: cuentasHeaders,
    isLoading: cuentasLoading,
    error: cuentasError,
    searchValue: cuentasSearchValue,
    setSearchValue: setCuentasSearchValue,
    onPageChange: handleCuentasPageChange,
    esAdminARToHyS,
    refreshContratos,
  } = useCuentasUsuario()

  const cuentasRows = useMemo(() => {
    return buildCuentasRows(contratos, {
      onEliminar: () => {},
    })
  }, [contratos])

  const handleAsociarNuevaCuenta = useCallback(() => {
    setIsModalAsociarOpen(true)
  }, [])

  const handleModalCancel = useCallback(() => {}, [])

  const showToast = useCallback((variant, message) => {
    setToast({ variant, message })
    setTimeout(() => setToast(null), 4000)
  }, [])

  const handleModalSubmit = useCallback(
    async (formData) => {
      setIsAsociandoCuenta(true)

      try {
        const emailUsuario = getEmailUsuario()

        const payload = buildAsociarCuentaPayload(formData, datosPersonales, emailUsuario)
        const result = await asociarCuenta(payload)

        if (result.code >= 200 && result.code < 300) {
          // Escenario 1: solicitud exitosa (200 OK, 201 Created, etc.) -> alert success
          setIsModalAsociarOpen(false)
          showToast('success', TOAST_MESSAGES.ASOCIAR_CUENTA_SUCCESS)
          // Refrescar la grilla para ver la solicitud pendiente
          refreshContratos()
        } else {
          // Escenario 2: error de negocio devuelto por el backend.
          // Mapeamos mensajes conocidos del backend a textos propios del frontend
          const backendMessage = (result.message || '').trim()

          let mensajeError
          if (Object.values(TOAST_MESSAGES.BACKEND_MESSAGES).includes(backendMessage)) {
            // CUIT no es cliente de Provincia ART
            mensajeError = TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR_CUIT_NO_CLIENTE
          } else if (backendMessage) {
            // Otro error de negocio con mensaje del backend
            mensajeError = TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR_NEGOCIO
          } else {
            // Escenario 3: error sin mensaje del backend (ej. error interno del servidor)
            mensajeError = TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR
          }

          showToast('error', mensajeError)
        }
      } catch (error) {
        // Escenario 4: excepción de red / timeout -> mensaje propio de conectividad
        console.error('Error al asociar cuenta:', error)
        showToast('error', TOAST_MESSAGES.ASOCIAR_CUENTA_ERROR_RED)
      } finally {
        setIsAsociandoCuenta(false)
      }
    },
    [datosPersonales, refreshContratos, showToast]
  )

  const datosPersonalesIniciales = useMemo(
    () => ({
      apellido: apellidoReal,
      nombre: nombreReal,
      tipoTelefonoPrincipal: tipoTelPrincipalReal,
      numeroTelefonoPrincipal: numTelPrincipalReal,
      tipoTelefonoAlternativo: tipoTelAlternativoReal,
      numeroTelefonoAlternativo: numTelAlternativoReal,
    }),
    [apellidoReal, nombreReal, tipoTelPrincipalReal, numTelPrincipalReal, tipoTelAlternativoReal, numTelAlternativoReal]
  )

  const {
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
    isFormValid,
    hasChanges,
    setTipoTelefonoPrincipal,
    setNumeroTelefonoPrincipal,
    setTipoTelefonoAlternativo,
    setNumeroTelefonoAlternativo,
    handleApellidoChange,
    handleNombreChange,
    handleBlur,
    handlePhoneDataChange,
    handleAltPhoneDataChange,
    getFieldError,
    validateAllFields,
    resetForm,
    syncInitialData,
  } = useMiPerfilForm(datosPersonalesIniciales)

  const correoElectronico = correoReal

  const handleVolver = () => {
    if (isMobile && openMobileProfile) {
      openMobileProfile()
      return
    }

    navigate('/')
  }

  const handleCancelar = () => {
    setIsCancelModalOpen(true)
  }

  const handleConfirmarDescartar = () => {
    resetForm()
    setIsCancelModalOpen(false)
  }

  const handleGuardarCambios = () => {
    const isValid = validateAllFields()

    if (!isValid) {
      return
    }

    setIsConfirmSaveModalOpen(true)
  }

  const handleConfirmSave = async () => {
    setIsConfirmSaveModalOpen(false)
    setIsSaving(true)

    try {
      const idUsuario = getIdUsuario()
      if (!idUsuario) {
        throw new Error('No se encontró el ID de usuario')
      }

      // Construir el payload con los datos del formulario
      const formData = {
        apellido,
        nombre,
        tipoTelefonoPrincipal,
        numeroTelefonoPrincipal,
        tipoTelefonoAlternativo,
        numeroTelefonoAlternativo,
      }
      const payload = buildDatosPersonalesPayload(formData, idUsuario)

      const result = await updateDatosPersonales(payload)

      if (result.code === 200) {
        updateDatosPersonalesState(formData)
        syncInitialData(formData)
        showToast('success', TOAST_MESSAGES.SUCCESS)
      } else {
        showToast('error', TOAST_MESSAGES.ERROR)
      }
    } catch (error) {
      console.error('Error al guardar datos personales:', error)
      showToast('error', TOAST_MESSAGES.ERROR)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCloseToast = () => {
    setToast(null)
  }

  return (
    <div className="min-h-[calc(100vh-168px)] px-4 md:px-0 pb-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="xs"
          startIcon={<Icon icon="keyboard_arrow_left" size="small" />}
          className="self-start leading-none"
          onClick={handleVolver}
        >
          Volver
        </Button>
      </div>

      <div className="mb-6">
        <Text variant="h6" className="text-primary-20 font-semibold">
          Mi perfil
        </Text>
      </div>

      <div className="bg-[#F5F5F5] rounded-lg p-6">
        <Text variant="h6" className="text-primary-10 font-semibold mb-6">
          Datos personales
        </Text>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-4">
            <div className="md:col-span-4">
              <Field label={FORM_LABELS.APELLIDO} errorMessage={getFieldError('apellido')} isRequired={true}>
                <InputV2
                  placeholder={FORM_PLACEHOLDERS.APELLIDO}
                  value={apellido}
                  onChange={handleApellidoChange}
                  onBlur={() => handleBlur('apellido', apellido)}
                  startIcon=""
                  endIcon=""
                />
              </Field>
            </div>

            <div className="md:col-span-4">
              <Field label={FORM_LABELS.NOMBRE} errorMessage={getFieldError('nombre')} isRequired={true}>
                <InputV2
                  placeholder={FORM_PLACEHOLDERS.NOMBRE}
                  value={nombre}
                  onChange={handleNombreChange}
                  onBlur={() => handleBlur('nombre', nombre)}
                  startIcon=""
                  endIcon=""
                />
              </Field>
            </div>

            <div className="md:col-span-4">
              <Field
                variant="email"
                label={FORM_LABELS.CORREO_ELECTRONICO}
                placeholder={FORM_PLACEHOLDERS.CORREO_ELECTRONICO}
                value={correoElectronico}
                onChange={() => {}}
                isDisabled={true}
                noVariantIcon
                noVariantHints
              />
            </div>

            <div className="md:col-span-6">
              <Field label={FORM_LABELS.TIPO_TELEFONO_PRINCIPAL} isRequired={true} className="[&_label]:mb-0.3">
                <InputDropdown
                  placeholder={FORM_PLACEHOLDERS.SELECCIONAR}
                  value={tipoTelefonoPrincipal}
                  defaultSelection={false}
                  onChange={(e) => {
                    const newValue = e?.value ?? ''
                    setTipoTelefonoPrincipal(newValue)
                  }}
                  fullWidth
                >
                  {TIPOS_TELEFONO.filter((opt) => opt.id !== '').map((opt) => (
                    <InputDropdown.Option
                      key={opt.id}
                      value={opt.descripcion}
                      selected={opt.descripcion === tipoTelefonoPrincipal}
                    >
                      {opt.descripcion}
                    </InputDropdown.Option>
                  ))}
                </InputDropdown>
              </Field>
            </div>

            <div className="md:col-span-6">
              <Field
                variant="phone"
                label={FORM_LABELS.NUMERO_TELEFONO_PRINCIPAL}
                placeholder={FORM_PLACEHOLDERS.NUMERO_TELEFONO_PRINCIPAL}
                value={numeroTelefonoPrincipal}
                onChange={(value) => setNumeroTelefonoPrincipal(value)}
                onPhoneDataChange={handlePhoneDataChange}
                noVariantIcon
                isRequired={true}
                className="[&_button]:hidden"
              />
            </div>

            <div className="md:col-span-6 -mt-2 [&_label]:mb-1">
              <InputDropdown
                placeholder={FORM_PLACEHOLDERS.SELECCIONAR}
                label={FORM_LABELS.TIPO_TELEFONO_ALTERNATIVO}
                value={tipoTelefonoAlternativo}
                defaultSelection={false}
                onChange={(e) => {
                  const rawValue = e?.value ?? ''
                  // "Seleccionar" equivale a vacío: el tipo alternativo vuelve a considerarse sin cargar
                  const newValue = rawValue === 'Seleccionar' ? '' : rawValue
                  setTipoTelefonoAlternativo(newValue)
                }}
                fullWidth
              >
                {TIPOS_TELEFONO.map((opt) => (
                  <InputDropdown.Option
                    key={opt.id}
                    value={opt.descripcion}
                    selected={opt.descripcion === tipoTelefonoAlternativo}
                  >
                    {opt.descripcion}
                  </InputDropdown.Option>
                ))}
              </InputDropdown>
            </div>

            <div className="md:col-span-6">
              <Field
                variant="phone"
                label={FORM_LABELS.NUMERO_TELEFONO_ALTERNATIVO}
                placeholder={FORM_PLACEHOLDERS.NUMERO_TELEFONO_ALTERNATIVO}
                value={numeroTelefonoAlternativo}
                onChange={(value) => setNumeroTelefonoAlternativo(value)}
                onPhoneDataChange={handleAltPhoneDataChange}
                noVariantIcon
              />
            </div>
          </div>
        </div>
      </div>

      {esAdminARToHyS === false && (
        <div className="bg-[#F5F5F5] rounded-lg p-6 mt-6">
          <Text variant="h6" className="text-primary-10 font-semibold mb-6">
            Mis cuentas
          </Text>

          <div className="bg-white rounded-lg p-6 border border-gray-200">
            {/* Barra de búsqueda y botón */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <SafeSearchBar
                placeholder="Buscar por razón social, nombre o CUIT"
                searchValue={cuentasSearchValue}
                setSearchValue={setCuentasSearchValue}
                opciones={[]}
                setSelected={() => {}}
                cargando={false}
                noResult={false}
                classNameContainer="w-full md:w-[420px]"
              />
              <Button
                variant="ghost"
                size="sm"
                startIcon={<Icon icon="add" size="small" />}
                onClick={handleAsociarNuevaCuenta}
                className="text-primary-10 whitespace-nowrap"
              >
                Asociar nueva cuenta
              </Button>
            </div>

            {/* Tabla de cuentas */}
            <CuentasTable
              headers={cuentasHeaders}
              tableData={cuentasRows}
              onPageChange={handleCuentasPageChange}
              isLoading={cuentasLoading}
              hasSearchFilter={cuentasSearchValue.trim().length > 0}
              error={cuentasError}
            />
          </div>
        </div>
      )}

      <div className="flex mt-6 justify-end gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={handleCancelar} disabled={isSaving}>
          Cancelar
        </Button>

        <PrimaryButton onClick={handleGuardarCambios} size="small" disabled={!isFormValid || !hasChanges || isSaving}>
          {isSaving ? 'Guardando...' : 'Guardar cambios'}
        </PrimaryButton>
      </div>

      {/* Modal de confirmación para guardar cambios */}
      <ModalGuardarCambios
        open={isConfirmSaveModalOpen}
        setOpen={setIsConfirmSaveModalOpen}
        onCancel={() => setIsConfirmSaveModalOpen(false)}
        onConfirm={handleConfirmSave}
      />

      {/* Modal de descartar cambios */}
      <ModalDescartarCambios
        open={isCancelModalOpen}
        setOpen={setIsCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        onConfirm={handleConfirmarDescartar}
      />

      {/* Toast único con variant dinámico: success (solicitud exitosa) o error (los 3 escenarios de error) */}
      {toast &&
        createPortal(
          <div className="fixed top-4 right-4 w-[400px]" style={{ zIndex: 99999 }}>
            <Alert
              variant={toast.variant}
              iconStyle="default"
              title=""
              description={toast.message}
              descriptionStyle="whitespace-normal break-normal"
              interactive={true}
              buttonText=""
              onClose={handleCloseToast}
              containerStyle="!max-w-none w-full"
            />
          </div>,
          document.body
        )}

      <ModalAsociarCuenta
        open={isModalAsociarOpen}
        setOpen={setIsModalAsociarOpen}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
        isSubmitting={isAsociandoCuenta}
        cargoOptions={cargoOptions}
        areaOptions={areaOptions}
      />
    </div>
  )
}
